"use strict";

const InputBaseControlBooleanComponent = require("@gluenetworks/input_base_control").Boolean;
const Renderer = require("./InputCheckboxControlRenderer");
const _ = require("lodash");

class InputCheckboxControlComponent extends InputBaseControlBooleanComponent {
    /**
     * @schema.name Input Checkbox Control
     * @schema.version 2.0.0
     * @schema.package @gluenetworks/input_checkbox_control
     * @schema.namespace require("@gluenetworks/steve_ui_core").Control.Boolean.InputCheckbox
     * @schema.events ["Disabled", "Value", "Visible", "Invalid", "DarkTheme", "Theme", "VariedChecked", "Label", "LabelShown", "Borderless", "Title", "Focus", "Blur", "SetAttribute", "UserValue", "UserFocus", "UserBlur", "UserKeyDown"]
     *
     * @schema.configuration.variedChecked.name variedChecked
     * @schema.configuration.variedChecked.type boolean
     * @schema.configuration.variedChecked.default false
     * @schema.configuration.variedChecked.required false
     *
     * @schema.configuration.labelShown.name labelShown
     * @schema.configuration.labelShown.type boolean
     * @schema.configuration.labelShown.default false
     * @schema.configuration.labelShown.required false
     *
     * @schema.configuration.borderless.name borderless
     * @schema.configuration.borderless.type boolean
     * @schema.configuration.borderless.default false
     * @schema.configuration.borderless.required false
     *
     * @schema.configuration.labelFirst.name labelFirst
     * @schema.configuration.labelFirst.type boolean
     * @schema.configuration.labelFirst.default false
     * @schema.configuration.labelFirst.required false
     *
     */

    constructor(configOptions) {
        super(configOptions);

        this._variedChecked = _.get(configOptions, "variedChecked", false);
        this._labelShown = _.get(configOptions, "labelShown", false);
        this._borderless = _.get(configOptions, "borderless", false);
        this._labelFirst = _.get(configOptions, "labelFirst", false);
        this._type = _.get(configOptions, "type", false);

        this._renderer = new Renderer(this);

        this._registerListeners();
    }
    /*******************************************/
    /* START PRIVATE COMPONENT MODEL METHODS */
    /*******************************************/
    _registerListeners() {}
    _unregisterListeners() {}
    /*******************************************/
    /* END PRIVATE COMPONENT MODEL METHODS */
    /* START PUBLIC COMPONENT MODEL METHODS */
    /*******************************************/
    get renderer() {
        return this.getRenderer();
    }
    getRenderer() {
        return this._renderer;
    }
    shutdown() {
        this._unregisterListeners();
        let prom = this._renderer._shutdown();
        this._renderer = undefined;
        this._invalid = undefined;
        this._value = undefined;
        this._visible = undefined;
        this._disabled = undefined;
        this._variedChecked = undefined;
        this._darkTheme = undefined;
        this._theme = undefined;
        this._labelShown = undefined;
        this._label = undefined;
        this._labelFirst = undefined;
        return prom;
    }
    /*******************************************/
    /* END COMPONENT MODEL METHODS */
    /* START HANDLER METHODS */
    /*******************************************/

    /*******************************************/
    /* END HANDLER METHODS */
    /* START PRIVATE METHODS */
    /*******************************************/

    /*******************************************/
    /* END PRIVATE METHODS */
    /* START PUBLIC API METHODS */
    /*******************************************/

    /**
     * @schema.methods.setLabelFirst.name setLabelFirst
     * @schema.methods.setLabelFirst.description if true and label shown then label is displayed to the left of checkbox
     *                                           else label is displayed to the right of checkbox
     * @schema.methods.setLabelFirst.returns Instance of the control
     * @schema.methods.setLabelFirst.parameters.newVal.name newVal
     * @schema.methods.setLabelFirst.parameters.newVal.description Sets if label is shown to the left of checkbox
     * @schema.methods.setLabelFirst.parameters.newVal.type boolean
     */
    setLabelFirst(newVal) {
        if (newVal !== this._labelFirst && _.isBoolean(newVal)) {
            this._labelFirst = newVal;
            this.emit("LabelFirst", this._labelFirst);
            return this;
        }
    }

    /**
     * @schema.methods.isLabelFirst.name isLabelFirst
     * @schema.methods.isLabelFirst.description Returns if the label is shown to the left of checkbox
     * @schema.methods.isLabelFirst.returns boolean
     */
    isLabelFirst() {
        return this._labelFirst;
    }

    set labelFirst(newVal) {
        this.setLabelFirst(newVal);
    }

    get labelFirst() {
        return this.isLabelFirst();
    }

    /**
     * @schema.methods.setLabelShown.name setLabelShown
     * @schema.methods.setLabelShown.description Sets the shown state of the label and returns an instance of the control for chainability
     * @schema.methods.setLabelShown.returns Instance of the control
     * @schema.methods.setLabelShown.parameters.newVal.name newVal
     * @schema.methods.setLabelShown.parameters.newVal.description The shown state of the label
     * @schema.methods.setLabelShown.parameters.newVal.type boolean
     */
    setLabelShown(newVal) {
        this._labelShown = newVal;
        this.emit("LabelShown", this._labelShown);
        return this;
    }
    /**
     * @schema.methods.isLabelShown.name isLabelShown
     * @schema.methods.isLabelShown.description Returns the shown state of the label
     * @schema.methods.isLabelShown.returns boolean
     */
    isLabelShown() {
        return this._labelShown;
    }
    get labelShown() {
        return this.isLabelShown();
    }
    set labelShown(newVal) {
        this.setLabelShown(newVal);
    }

    /**
     * @schema.methods.setVariedChecked.name setVariedChecked
     * @schema.methods.setVariedChecked.description Sets the varied checked state (not fully checked) and returns an instance of the control for chainability
     * @schema.methods.setVariedChecked.returns Instance of the control
     * @schema.methods.setVariedChecked.parameters.newVal.name newVal
     * @schema.methods.setVariedChecked.parameters.newVal.description The varied checked state of the control
     * @schema.methods.setVariedChecked.parameters.newVal.type boolean
     */
    setVariedChecked(newVal) {
        this._variedChecked = newVal;
        this.emit("VariedChecked", this._variedChecked);
        return this;
    }
    /**
     * @schema.methods.isVariedChecked.name isVariedChecked
     * @schema.methods.isVariedChecked.description Returns the varied checked state of the control
     * @schema.methods.isVariedChecked.returns boolean
     */
    isVariedChecked() {
        return this._variedChecked;
    }
    get variedChecked() {
        return this.isVariedChecked();
    }
    set variedChecked(newVal) {
        this.setVariedChecked(newVal);
    }

    /**
     * @schema.methods.setBorderless.name setBorderless
     * @schema.methods.setBorderless.description Sets the borderless state (check icon with no bounding rectangle) and returns an instance of the control for chainability
     * @schema.methods.setBorderless.returns Instance of the control
     * @schema.methods.setBorderless.parameters.newVal.name newVal
     * @schema.methods.setBorderless.parameters.newVal.description The borderless state of the control
     * @schema.methods.setBorderless.parameters.newVal.type boolean
     */
    setBorderless(newVal) {
        this._borderless = newVal;
        this.emit("Borderless", this._borderless);
        return this;
    }
    /**
     * @schema.methods.isBorderless.name isBorderless
     * @schema.methods.isBorderless.description Returns the borderless state of the control
     * @schema.methods.isBorderless.returns boolean
     */
    isBorderless() {
        return this._borderless;
    }
    get borderless() {
        return this.isBorderless();
    }
    set borderless(newVal) {
        this.setBorderless(newVal);
    }
    /*******************************************/
    /* END PUBLIC API METHODS */
    /*******************************************/
}
module.exports = InputCheckboxControlComponent;
